import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { formatErr } from "../lib/auth";
import { Calendar as CalendarIcon, Clock, Video, Phone, Check, ArrowRight } from "lucide-react";

function formatDate(d) { return d.toISOString().slice(0, 10); }
function buildDates(start, count) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    if (d.getDay() !== 0) out.push(d); // skip sundays
  }
  return out;
}

const SERVICES = [
  "Credit Restoration Consulting",
  "Credit-Building Strategy",
  "Budget & DTI Analysis",
  "Dispute Support & Coaching",
  "Lender-Ready Preparation",
  "Not sure yet — need guidance",
];

export default function Booking() {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [date, setDate] = useState(formatDate(today));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    service: SERVICES[0], meeting_type: "zoom", goal: "",
  });

  const dates = buildDates(today, 21);

  useEffect(() => {
    api.get(`/bookings/availability?date=${date}`).then(({ data }) => setSlots(data));
    setTime("");
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!time) { setErr("Please select a time slot."); return; }
    setLoading(true);
    try {
      const referral_code = (await import("../lib/referral")).getStoredReferral() || undefined;
      const { data } = await api.post("/bookings", { ...form, date, time, referral_code });
      setConfirmed(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  if (confirmed) {
    return (
      <section className="section hero-radial">
        <div className="container-if max-w-2xl card-cream p-10 text-[var(--if-ink)]">
          <div className="w-12 h-12 rounded-full bg-[var(--if-olive)] flex items-center justify-center mb-5">
            <Check className="w-6 h-6 text-[var(--if-cream)]" />
          </div>
          <h1 className="font-display text-4xl mb-3">Mission briefing confirmed.</h1>
          <p className="text-[rgba(27,29,25,0.7)] mb-6">
            You're locked in, {confirmed.first_name}. We'll be ready at the agreed time. Expect a reminder shortly.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div><div className="font-mono-label text-[var(--if-olive-deep)]">Date</div><div className="font-display text-lg">{confirmed.date}</div></div>
            <div><div className="font-mono-label text-[var(--if-olive-deep)]">Time</div><div className="font-display text-lg">{confirmed.time}</div></div>
            <div><div className="font-mono-label text-[var(--if-olive-deep)]">Service</div><div className="text-base">{confirmed.service}</div></div>
            <div><div className="font-mono-label text-[var(--if-olive-deep)]">Meeting</div><div className="text-base capitalize">{confirmed.meeting_type.replace("_", " ")}</div></div>
          </div>
          {confirmed.meeting_link && (
            <div className="p-4 rounded-lg bg-[rgba(47,58,37,0.1)] border border-[var(--if-olive)] text-sm" data-testid="booking-meeting-link">
              <div className="font-mono-label text-[var(--if-olive-deep)] mb-1">Meeting Link</div>
              <a href={confirmed.meeting_link} className="text-[var(--if-olive-deep)] underline break-all" target="_blank" rel="noreferrer">
                {confirmed.meeting_link}
              </a>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="section hero-radial min-h-[80vh]">
      <div className="container-if">
        <div className="font-mono-label text-[var(--if-gold)] mb-3">Book Your Session</div>
        <h1 className="font-display text-[44px] md:text-[64px] leading-[0.95] mb-4">
          Schedule your <span className="italic text-[var(--if-gold)]">free consultation</span>.
        </h1>
        <p className="text-[rgba(35,38,28,0.7)] max-w-2xl mb-12">
          Pick a date, choose a time slot, and we'll meet you on Zoom, Google Meet, or by phone.
          Response within 24 hours · CROA compliant · 90-day money-back guarantee.
        </p>

        <div className="grid lg:grid-cols-[1fr,1.1fr] gap-8">
          <div className="card-dark p-7">
            <div className="flex items-center gap-2 mb-4">
              <CalendarIcon className="w-4 h-4 text-[var(--if-gold)]" />
              <span className="font-mono-label text-[var(--if-gold)]">Select a date</span>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2 mb-8">
              {dates.map((d) => {
                const v = formatDate(d);
                const active = v === date;
                return (
                  <button
                    key={v}
                    onClick={() => setDate(v)}
                    className={`p-2 rounded-lg text-center transition-all border ${active ? "bg-[var(--if-gold)] text-[#1a1a17] border-[var(--if-gold)]" : "border-[var(--if-border)] hover:border-[var(--if-gold)]"}`}
                    data-testid={`date-${v}`}
                  >
                    <div className="text-[10px] font-mono-label opacity-70">{d.toLocaleDateString("en", { weekday: "short" })}</div>
                    <div className="font-display text-lg">{d.getDate()}</div>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-[var(--if-gold)]" />
              <span className="font-mono-label text-[var(--if-gold)]">Available times (CT)</span>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {slots.map((s) => {
                const cls =
                  time === s.time
                    ? "bg-[var(--if-gold)] text-[#1a1a17] border-[var(--if-gold)]"
                    : s.available
                      ? "border-[var(--if-border)] hover:border-[var(--if-gold)] text-[var(--if-cream)]"
                      : "border-[rgba(35,38,28,0.05)] text-[rgba(35,38,28,0.2)] line-through cursor-not-allowed";
                return (
                  <button
                    key={s.time}
                    disabled={!s.available}
                    onClick={() => setTime(s.time)}
                    className={`p-2.5 rounded-lg text-sm border transition-all ${cls}`}
                    data-testid={`slot-${s.time}`}
                  >
                    {s.time}
                  </button>
                );
              })}
            </div>
          </div>

          <form onSubmit={submit} className="card-dark p-7 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <input required className="if-input" placeholder="First name" value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="book-first-name" />
              <input required className="if-input" placeholder="Last name" value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="book-last-name" />
            </div>
            <input required type="email" className="if-input" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="book-email" />
            <input required className="if-input" placeholder="Phone" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="book-phone" />
            <select className="if-input" value={form.service}
              onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="book-service">
              {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>

            <div>
              <div className="font-mono-label text-[rgba(35,38,28,0.6)] mb-2">Meeting type</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "zoom", label: "Zoom", icon: Video },
                  { id: "google_meet", label: "Google Meet", icon: Video },
                  { id: "phone", label: "Phone", icon: Phone },
                ].map((m) => (
                  <button type="button" key={m.id}
                    onClick={() => setForm({ ...form, meeting_type: m.id })}
                    className={`p-3 rounded-lg border flex flex-col items-center gap-1 text-xs transition-all ${
                      form.meeting_type === m.id ? "border-[var(--if-gold)] bg-[rgba(201,168,106,0.08)]" : "border-[var(--if-border)] hover:border-[var(--if-gold)]"
                    }`}
                    data-testid={`book-mtg-${m.id}`}
                  >
                    <m.icon className="w-4 h-4 text-[var(--if-gold)]" /> {m.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea className="if-input" rows={3} placeholder="What are you trying to accomplish? (optional)"
              value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} data-testid="book-goal" />

            {err && <div className="text-red-400 text-sm" data-testid="book-err">{err}</div>}

            <button type="submit" disabled={loading} className="btn-gold justify-center mt-1" data-testid="book-submit">
              {loading ? "Booking…" : <>Book My Session <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-xs text-[rgba(35,38,28,0.45)] text-center">Response within 24 hours. CROA compliant. 90-day money-back guarantee.</p>
          </form>
        </div>
      </div>
    </section>
  );
}
